import i18next from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';

/**
 * 가격 등록 공급가(197)·할인율(198)·판매가(199) 요청 — 레거시 appr_edit_forms_eas_fnco_price_supply·_discount·_sale.
 * EAS `reqno` 진입 전용. 세 양식은 표 열·제목(198 은 표 아래 안내 문구도)만 다르고 조회·저장·검사는 같음
 */
export const FNCO_PRICE_SUPPLY_TYPE = 197;
export const FNCO_PRICE_DISCOUNT_TYPE = 198;
export const FNCO_PRICE_SALE_TYPE = 199;

export const FNCO_PRICE_TYPES = [
  FNCO_PRICE_SUPPLY_TYPE,
  FNCO_PRICE_DISCOUNT_TYPE,
  FNCO_PRICE_SALE_TYPE,
] as const;

export type FncoPriceType = (typeof FNCO_PRICE_TYPES)[number];

export const isFncoPriceType = (apvType: number): apvType is FncoPriceType =>
  (FNCO_PRICE_TYPES as readonly number[]).includes(apvType);

/** 표 제목 — 번역 key */
export const FNCO_PRICE_TITLE: Record<FncoPriceType, string> = {
  [FNCO_PRICE_SUPPLY_TYPE]: '가격 등록 공급가 목록',
  [FNCO_PRICE_DISCOUNT_TYPE]: '가격 등록 할인율 목록',
  [FNCO_PRICE_SALE_TYPE]: '가격 등록 판매가 목록',
};

const FNCO_PRICE_SUBJECT: Record<FncoPriceType, string> = {
  [FNCO_PRICE_SUPPLY_TYPE]: '가격 등록 공급가 요청의 건',
  [FNCO_PRICE_DISCOUNT_TYPE]: '가격 등록 할인율 요청의 건',
  [FNCO_PRICE_SALE_TYPE]: '가격 등록 판매가 요청의 건',
};

/** 레거시는 편집으로 다시 열어도 이 문구로 덮어씀 */
export const buildFncoPriceSupplySubject = (apvType: FncoPriceType): string =>
  i18next.t(FNCO_PRICE_SUBJECT[apvType]) || FNCO_PRICE_SUBJECT[apvType];

export const FNCO_PRICE_SUPPLY_MISSING_MESSAGE =
  '데이터의 request number가 존재하지 않습니다. reqno를 다시 확인해주세요.';
export const FNCO_PRICE_SUPPLY_LOADING_MESSAGE =
  '데이터를 불러오는 중입니다. 잠시 후 다시 시도해주세요.';

/** 이 행 수를 넘으면 나머지는 접어 두고 「펼치기」 */
export const FNCO_PRICE_SUPPLY_VISIBLE_ROWS = 20;

export type FncoPriceSupplyItem = {
  KUNNR?: string;
  NAME1?: string;
  MATNR?: string;
  MAKTX?: string;
  KBETR_P?: string;
  KBETR_S?: string;
  KONWA_S?: string;
  KBETR?: string;
  KBETR2?: string;
  KONWA?: string;
};

/** SAP ET_RESULT 원문 행 — 저장 attributes.data 에 손대지 않고 그대로 보관 */
export type FncoPriceSupplyData = object[];

export type FncoPriceSupplyState = {
  requestNumber: string | null;
  /** 조회 실패면 null — 실패해도 상신은 막지 않음(레거시 동일) */
  data: FncoPriceSupplyData | null;
  loading: boolean;
};

export const createFncoPriceSupplyState = (
  requestNumber: string | null = null,
): FncoPriceSupplyState => ({ requestNumber, data: null, loading: false });

export type FncoPriceSupplyDraftSource = {
  code: number;
  attributes?: { requestNumber?: string; data?: FncoPriceSupplyData | null };
  display?: { subject?: string; source?: FncoPriceSupplyData | null };
};

/** 레거시처럼 첫 건의 ITEM 만 표로 */
export const getFncoPriceSupplyItems = (
  data: FncoPriceSupplyData | null | undefined,
): FncoPriceSupplyItem[] => {
  const first: { ITEM?: unknown } | undefined = Array.isArray(data)
    ? data[0]
    : undefined;
  const items = first?.ITEM;
  return Array.isArray(items) ? (items as FncoPriceSupplyItem[]) : [];
};

/** 진입 쿼리 → 요청번호. 반복 키는 첫 값, 공백만 있으면 없음 */
export const getFncoPriceSupplyEntryRequestNumber = (
  query: Record<string, string | string[] | undefined>,
): string | undefined => {
  const raw = query.reqno;
  const value = (Array.isArray(raw) ? raw[0] : raw)?.trim();
  return value || undefined;
};

export const parseFncoPriceSupplyRequestNumber = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => {
  const value = attributes?.requestNumber;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

export const parseFncoPriceSupplyContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => {
  const content = attributes?.content;
  return typeof content === 'string' && content.trim() ? content : null;
};

/** 임시저장·상신 공통 — 요청번호 없음·조회 중만 막음 */
export const checkFncoPriceSupplyBeforeSave = (
  state: FncoPriceSupplyState | null | undefined,
): string | null => {
  if (!state?.requestNumber) return FNCO_PRICE_SUPPLY_MISSING_MESSAGE;
  if (state.loading) return FNCO_PRICE_SUPPLY_LOADING_MESSAGE;
  return null;
};

/** 저장 attributes — content 는 표 제목 + 전체 행 표 HTML */
export const buildFncoPriceSupplyAttributes = (
  state: FncoPriceSupplyState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state?.requestNumber
    ? { requestNumber: state.requestNumber, content, data: state.data }
    : null;
